<template>
  <div>
    <!--    title-->
    <v-row
      style="border-bottom: 1px solid #E5E7EB;"
      class="margin-0"
    >
      <v-col
        class="d-flex justify-start block-title"
      >
        <div
          class="block-title--wrapper"
        >
          {{ $t('fleet/vehicle_range_limit.range_limits') }}
        </div>
      </v-col>

      <v-col
        class="d-flex justify-end"
      >
        <active-icon
          :cell-data="item.status"
          class="mr-2"
        />

        <table-row-actions-menu
          :data-index="itemIndex"
          :config="actionsConfig"
          :table-data="data"
        />
      </v-col>
    </v-row>

    <!--    visible data section-->
    <v-row
      v-for="(dataObject, dataObjIndex) in getVisibleItemDetails()"
      :key="dataObjIndex"
      class="margin-0"
    >
      <v-col
        class="data-label"
      >
        {{ dataObject.label }}
      </v-col>

      <v-col
        class="d-flex justify-end data-value"
      >
        {{ dataObject.value }}
      </v-col>
    </v-row>

    <!--    hidden data section-->
    <template
      v-if="slidedDown"
    >
      <v-row
        v-for="(dataObject, dataObjIndex) in getAllItemDetails()"
        :key="'hidden_' + dataObjIndex"
        class="margin-0"
      >
        <v-col
          class="data-label"
        >
          {{ dataObject.label }}
        </v-col>

        <v-col
          class="d-flex justify-end data-value"
        >
          <template
            v-if="dataObject.key === 'alarm_on'"
          >
            <cell-icon
              :cell-data="dataObject.value"
              :config="dataObject"
              :row-data="item"
            />
          </template>
          <template
            v-else
          >
            {{ dataObject.value }}
          </template>
        </v-col>
      </v-row>
    </template>

    <!--    arrow up/down button for showing more details-->
    <image-field
      :src="slidedDown ? '/img/icons/arrow-up-base.svg' : '/img/icons/arrow-down-base.svg'"
      class="arrowUpDown"
      @onClick="slidedDown = !slidedDown"
    />

    <information-dialog
      :open-dialog="informationDialogConfig.openDialog"
      :title="informationDialogConfig.title"
      :dialog-message="informationDialogConfig.message"
      :positive-button-text="informationDialogConfig.positiveBtnText"
      :negative-button-text="informationDialogConfig.negativeBtnText"
      :confirm-callback-function="informationDialogConfig.callbackFn"
      @close="$set(informationDialogConfig, 'openDialog', false)"
    />
  </div>
</template>

<script>
import ImageField from '@/global/components/fields/ImageField.vue'
import ActiveIcon from '../../components/main-table/ActiveIcon.vue'
import TableRowActionsMenu from '@/global/components/table/components/TableRowActionsMenu.vue'
import InformationDialog from '@/global/components/dialogs/InformationDialog.vue'
import { dateService, FORMATS } from '@/global/services/date'
import CellIcon from '../../components/main-table/CellIcon.vue'
import previewFiles from '../../helpers/previewFiles'
import vehicleRangeMixin from '../../mixins/vehicleRangeMixin'
import { showDetailsAction, deleteAction, activationAction } from '@/global/components/table/helpers/tableActions'

const DEFAULT_EMPTY_VALUE = '--'
export default {
  name: 'VehicleRangeLimitGridBlock',

  components: {
    CellIcon,
    InformationDialog,
    TableRowActionsMenu,
    ActiveIcon,
    ImageField
  },

  mixins: [
    previewFiles,
    vehicleRangeMixin
  ],

  props: {
    data: {
      type: Array,
      default: () => ([])
    },
    item: {
      type: Object,
      required: true,
      default: () => ({})
    },
    itemIndex: {
      type: Number,
      default: 0
    },
    blockConfig: {
      type: Object,
      default: () => ({})
    }
  },

  data () {
    return {
      slidedDown: false,
      informationDialogConfig: {
        openDialog: false,
        message: this.$t('fleet/vehicle.confirm_delete'),
        title: '',
        positiveBtnText: '',
        negativeBtnText: this.$t('base.cancel'),
        callbackFn: () => {}
      }
    }
  },

  computed: {
    actionsConfig () {
      return {
        tableConfig: {
          actionsConfig: [
            showDetailsAction(this.$t, (event) => this.onShowRangeDetails(event)),
            activationAction(this.$t, (event) => this.onRangeLimitActivation(event), (event) => !!event?.data?.status),
            deleteAction(this.$t, (event) => this.onRangeLimitDelete(event))
          ]
        }
      }
    }
  },

  methods: {
    getVisibleItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_range_limit.limit_type'),
          value: this.item?.limit_type || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_range_limit.input_date'),
          value: this.item?.input_date ? dateService.formatFromUtc(this.item.input_date, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_range_limit.yearly_limit'),
          value: this.item?.yearly_limit || DEFAULT_EMPTY_VALUE
        }
      ]
    },

    getAllItemDetails () {
      return [
        {
          label: this.$t('fleet/vehicle_range_limit.monthly_limit'),
          value: this.item?.monthly_limit || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_range_limit.valid_until_km'),
          value: this.item?.valid_until_km || DEFAULT_EMPTY_VALUE
        },
        {
          label: this.$t('fleet/vehicle_range_limit.valid_until_date'),
          value: this.item?.valid_until_date ? dateService.formatFromUtc(this.item.valid_until_date, FORMATS.date) : DEFAULT_EMPTY_VALUE
        },
        {
          key: 'alarm_on',
          label: this.$t('fleet/vehicle_document.alarm_on'),
          value: this.item?.alarm_on,
          src: this.item?.alarm_on === 1 ? '/img/icons/green-check-circle.svg' : '/img/icons/grey-check-circle.svg'
        }
      ]
    }
  }
}
</script>

<style scoped lang="scss">
.block-title {
  display: flex;
  justify-content: flex-start;
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: 0;
  color: #111827;
  align-items: center;
  padding-top: 0;
  padding-bottom: 0;
}

.block-title--wrapper {
  display: flex;
  height: 48px;
  align-items: center;
}

.data-label {
  font-family: 'Satoshi', sans-serif;
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #6B7280;
}

.data-value {
  font-family: 'Satoshi', sans-serif;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  letter-spacing: 0;
  color: #111827;
  text-align: right;
}

.arrowUpDown {
  position: absolute;
  bottom: 10px;
  left: 50%;
}

.arrowUpDown:hover {
  cursor: pointer;
}
</style>
